/**
 * Represents possible events that EventHub can handle.
 * @private
 */
export const EventName = {
    /* Initialize event */
    INIT: "init",
    /* Event is a network request */
    NETWORK: 'network',
    /* Event is a cache read/write */
    CACHE: 'cache',
    /* Event is a subscription to given event type */
    SUBSCRIBE: 'subscribe',
    /* Event is of type publish */
    PUBLISH: 'publish'
};

/**
 * Represents the type of network request.
 * @private
 */
export const EventType = {
    GET: 'get',
    POST: 'post',
    PATCH: 'patch',
    DELETE: 'delete',
    READ_CACHE: 'readCache',
    WRITE_CACHE: 'writeCache',
    CLEAR_CACHE: 'clearCache',
    INIT: 'init',
    LOGIN_LOGOUT: 'loginLogout'
};

/**
 * Represents base event data.
 * @private
 * @constructor
 * @param {EventType} eventType - Type of event.
 */
export class EventData {
    constructor(eventType) {
        this.eventType = eventType;
    }
}

/**
 * Represents network event data.
 * @private
 * @constructor
 * @param {EventType} eventType - Type of event.
 * @param {string} path - URL/filepath for network request.
 * @param {Object} queryData - Object with the query params.
 * @param {FormData} formData - Object of type FormData required for multipart request.
 * @param {boolean} isPatch - Boolean to indicate if network request is a patch request.
 * @param {boolean} isMultipart - Boolean to indicate if network request is multipart.
 * @param {boolean} isGateway - Boolean to indicate if this is a gateway request.
 * @param {boolean} isScheduled - Boolean to indicate if this event should be re-scheduled.
 * @param {number} frequency - Frequency in milliseconds at which the event should be re-scheduled. 
 * This should be non-zero for OBSERVER events.
 */
export class NetworkEventData extends EventData {
    constructor(eventType, path, queryData = null, formData = null, isPatch = false, isMultipart = false, isGateway = true, isScheduled = false, frequency = 0) {
        super(eventType)
        this.path = path;
        this.queryData = queryData;
        this.formData = formData;
        this.isPatch = isPatch;
        this.isMultipart = isMultipart;
        this.isGateway = isGateway;
        this.isScheduled = isScheduled;
        this.frequency = frequency;
    }
}

/**
 * Represents types of Cache keys.
 * @private
 */
export const CacheKeys = {
    DEVELOPER_TOKEN: 'devToken',
    AUTH_TOKEN: 'authToken',
    CUSTOMER: 'customer'
};

/**
 * Represents cache event data.
 * @private
 * @param {EventType} eventType - Type of event.
 * @param {CacheKeys} cacheKey - Key to access.
 * @param {string} cacheValue - Value to be stored.
 */
export class CacheEventData extends EventData {
    constructor(eventType, cacheKey, cacheValue) {
        super(eventType)
        this.cacheKey = cacheKey;
        this.cacheValue = cacheValue;
    }
}

/**
 * Represents a callback for event.
 * @constructor
 * @private
 * @param {Function} success - Function that accepts the result of successful event completion.
 * @param {Function} failure - Function that accepts the error of an unsuccessful event.
 */
export function CallBack(success, failure) {
    this.success = success;
    this.failure = failure;
}

/**
 * Represents an event.
 * @constructor
 * @private
 * @param {EventName} name - Name of the event.
 * @param {EventData} data - Metadata for the event.  
 * @param {CallBack} callback - Callback to receive success/failure responses.
 */
export class RCEvent {
    constructor(name, data, callback) {
        this.name = name;
        this.data = data;
        this.callback = callback;
    }
}

/**
 * Represents subscribe event data.
 * @private
 * @param {EventType} eventType - Type of event.
 * @param {boolean} once - Boolean to indicate if subscriber should be removed after first response.
 */
export class SubscribeEventData extends EventData {
    constructor(eventType, once) {
        super(eventType);
        this.once = once;
    }
}

/**
 * Represent publish event data.
 * @private
 * @param {EventType} eventType - Type of event.
 * @param {any} result - Response to be published.
 */
export class PublishEventData extends EventData {
    constructor(eventType, result) {
        super(eventType);
        this.result = result;
    }
}